<div
  appGlossScroll
  appModalOverlay
  [appModalOverlayLabel]="'editNavigation' | i18n"
  (modalOverlayDismiss)="cancelled.emit()"
  class="tw-fixed tw-inset-0 tw-z-[100] tw-flex tw-flex-col tw-overflow-y-auto tw-bg-dt-surface-page tw-px-7 tw-pb-8 tw-pt-[calc(1.5rem+var(--safe-area-top,0px))]"
>
  <!-- Screen-reader-only feedback channel for the keyboard reorder interaction (#4152) - sighted
       users see the row move, but nothing else here is exposed to assistive tech. -->
  <div class="cdk-visually-hidden" aria-live="polite" role="status">{{ liveAnnouncement }}</div>

  <div class="tw-relative tw-mb-6 tw-flex tw-items-center tw-justify-center">
    <p class="tw-font-montserrat tw-text-[22px] tw-font-bold tw-text-dt-text-headings">
      {{ "editNavigation" | i18n }}
    </p>
    <close-button
      class="tw-absolute tw-right-0"
      type="plain"
      [isMatDialog]="false"
      (closeClickedEvent)="cancelled.emit()"
    ></close-button>
  </div>

  <div class="tw-flex tw-flex-col tw-gap-5">
    <div class="tw-flex tw-flex-col tw-gap-2">
      <div class="tw-flex tw-items-baseline tw-gap-1.5">
        <p
          class="tw-font-montserrat tw-text-[12px] tw-font-semibold tw-uppercase tw-tracking-[0.7px] tw-text-dt-text-tertiary"
        >
          {{ "pinnedToTabBar" | i18n }}
        </p>
        <span class="tw-font-montserrat tw-text-[11px] tw-font-normal tw-text-dt-text-tertiary">
          ({{ "pinnedToTabBarMinHint" | i18n }})
        </span>
      </div>
      <div
        #pinnedListEl
        role="listbox"
        [attr.aria-label]="'navListPinned' | i18n"
        class="tw-flex tw-min-h-[54px] tw-flex-col tw-gap-2"
      >
        @for (item of pinned; track item.key; let idx = $index) {
          @if (dropTargetList === "pinned" && dropTargetIndex === idx) {
            <div
              class="tw-h-[54px] tw-w-full tw-shrink-0 tw-rounded-2xl tw-border-2 tw-border-dashed tw-border-teal-500/50 tw-bg-teal-500/10"
            ></div>
          }
          @if (bumpPreviewItem?.key !== item.key) {
            <div
              #rowEl
              [attr.data-key]="item.key"
              role="option"
              [attr.tabindex]="isLocked(item) ? -1 : 0"
              [attr.aria-selected]="grabbedKey === item.key"
              (pointerdown)="onPointerDown($event, item, rowEl)"
              (pointermove)="onPointerMove($event)"
              (pointerup)="onPointerUp($event)"
              (pointercancel)="onPointerUp($event)"
              (keydown)="onRowKeydown($event, item, 'pinned')"
              [style.position]="draggingKey === item.key ? 'fixed' : null"
              [style.top.px]="draggingKey === item.key ? dragTop : null"
              [style.left.px]="draggingKey === item.key ? dragLeft : null"
              [style.width.px]="draggingKey === item.key ? dragWidth : null"
              [ngClass]="{
                'tw-z-[110] tw-scale-105 !tw-shadow-2xl': draggingKey === item.key,
                'tw-cursor-grab': !isLocked(item),
                'tw-cursor-default tw-bg-dt-surface-sunken': isLocked(item),
                'tw-border tw-border-l tw-border-t tw-border-solid tw-border-white/70 dark:tw-border-white/[0.18] tw-backdrop-blur-[3px] tw-bg-white/40 dark:tw-bg-white/[0.06] tw-shadow-elevation-glass-panel':
                  !isLocked(item),
                'tw-ring-2 tw-ring-teal-500': grabbedKey === item.key,
              }"
              class="tw-touch-none tw-flex tw-h-[54px] tw-w-full tw-shrink-0 tw-items-center tw-justify-between tw-gap-4 tw-overflow-hidden tw-rounded-2xl tw-px-4 tw-transition-transform"
            >
              <div class="tw-flex tw-items-center tw-gap-4">
                <svg class="tw-h-6 tw-w-6 tw-shrink-0 tw-text-dt-text-headings">
                  <use [attr.xlink:href]="'images/sprite.svg#' + item.icon" />
                </svg>
                <span
                  class="tw-font-montserrat tw-text-[14px] tw-font-medium tw-text-dt-text-headings"
                >
                  {{ item.label | i18n }}
                </span>
              </div>
              @if (!isLocked(item)) {
                <svg class="tw-h-6 tw-w-6 tw-shrink-0 tw-text-dt-text-tertiary">
                  <use xlink:href="images/sprite.svg#drag-handle" />
                </svg>
              }
            </div>
          }
        }
        @if (dropTargetList === "pinned" && dropTargetIndex === pinned.length) {
          <div
            class="tw-h-[54px] tw-w-full tw-shrink-0 tw-rounded-2xl tw-border-2 tw-border-dashed tw-border-teal-500/50 tw-bg-teal-500/10"
          ></div>
        }
      </div>
    </div>

    <div class="tw-flex tw-flex-col tw-gap-2">
      <p
        class="tw-font-montserrat tw-text-[12px] tw-font-semibold tw-uppercase tw-tracking-[0.7px] tw-text-dt-text-tertiary"
      >
        {{ "availableFeatures" | i18n }}
      </p>
      <div
        #availableListEl
        role="listbox"
        [attr.aria-label]="'navListAvailable' | i18n"
        class="tw-flex tw-min-h-[54px] tw-flex-col tw-gap-2"
      >
        @for (item of available; track item.key; let idx = $index) {
          @if (dropTargetList === "available" && dropTargetIndex === idx) {
            <div
              class="tw-h-[54px] tw-w-full tw-shrink-0 tw-rounded-2xl tw-border-2 tw-border-dashed tw-border-teal-500/50 tw-bg-teal-500/10"
            ></div>
          }
          @if (bumpPreviewIndex === idx && bumpPreviewItem) {
            <div
              class="tw-flex tw-h-[54px] tw-w-full tw-shrink-0 tw-items-center tw-gap-4 tw-overflow-hidden tw-rounded-2xl tw-border-2 tw-border-dashed tw-border-teal-500/50 tw-bg-teal-500/10 tw-px-4 tw-opacity-70"
            >
              <svg class="tw-h-6 tw-w-6 tw-shrink-0 tw-text-teal-700">
                <use [attr.xlink:href]="'images/sprite.svg#' + bumpPreviewItem.icon" />
              </svg>
              <span class="tw-font-montserrat tw-text-[14px] tw-font-medium tw-text-teal-700">
                {{ bumpPreviewItem.label | i18n }}
              </span>
            </div>
          }
          <div
            #rowEl
            [attr.data-key]="item.key"
            role="option"
            tabindex="0"
            [attr.aria-selected]="grabbedKey === item.key"
            (pointerdown)="onPointerDown($event, item, rowEl)"
            (pointermove)="onPointerMove($event)"
            (pointerup)="onPointerUp($event)"
            (pointercancel)="onPointerUp($event)"
            (keydown)="onRowKeydown($event, item, 'available')"
            [style.position]="draggingKey === item.key ? 'fixed' : null"
            [style.top.px]="draggingKey === item.key ? dragTop : null"
            [style.left.px]="draggingKey === item.key ? dragLeft : null"
            [style.width.px]="draggingKey === item.key ? dragWidth : null"
            [ngClass]="{
              'tw-z-[110] tw-scale-105 !tw-shadow-2xl': draggingKey === item.key,
              'tw-ring-2 tw-ring-teal-500': grabbedKey === item.key,
            }"
            class="tw-touch-none tw-flex tw-h-[54px] tw-w-full tw-shrink-0 tw-cursor-grab tw-items-center tw-justify-between tw-gap-4 tw-overflow-hidden tw-rounded-2xl tw-border tw-border-l tw-border-t tw-border-white/70 dark:tw-border-white/[0.18] tw-backdrop-blur-[3px] tw-bg-white/40 dark:tw-bg-white/[0.06] tw-px-4 tw-transition-transform tw-shadow-elevation-glass-panel"
          >
            <div class="tw-flex tw-items-center tw-gap-4">
              <svg class="tw-h-6 tw-w-6 tw-shrink-0 tw-text-dt-text-headings">
                <use [attr.xlink:href]="'images/sprite.svg#' + item.icon" />
              </svg>
              <span
                class="tw-font-montserrat tw-text-[14px] tw-font-medium tw-text-dt-text-headings"
              >
                {{ item.label | i18n }}
              </span>
            </div>
            <svg class="tw-h-6 tw-w-6 tw-shrink-0 tw-text-dt-text-tertiary">
              <use xlink:href="images/sprite.svg#drag-handle" />
            </svg>
          </div>
        }
        @if (dropTargetList === "available" && dropTargetIndex === available.length) {
          <div
            class="tw-h-[54px] tw-w-full tw-shrink-0 tw-rounded-2xl tw-border-2 tw-border-dashed tw-border-teal-500/50 tw-bg-teal-500/10"
          ></div>
        }
        @if (bumpPreviewIndex === available.length && bumpPreviewItem) {
          <div
            class="tw-flex tw-h-[54px] tw-w-full tw-shrink-0 tw-items-center tw-gap-4 tw-overflow-hidden tw-rounded-2xl tw-border-2 tw-border-dashed tw-border-teal-500/50 tw-bg-teal-500/10 tw-px-4 tw-opacity-70"
          >
            <svg class="tw-h-6 tw-w-6 tw-shrink-0 tw-text-teal-700">
              <use [attr.xlink:href]="'images/sprite.svg#' + bumpPreviewItem.icon" />
            </svg>
            <span class="tw-font-montserrat tw-text-[14px] tw-font-medium tw-text-teal-700">
              {{ bumpPreviewItem.label | i18n }}
            </span>
          </div>
        }
      </div>
    </div>

    <div class="tw-flex tw-gap-2">
      <button
        type="button"
        class="tw-flex tw-h-12 tw-flex-1 tw-items-center tw-justify-center tw-rounded-2xl tw-border-none tw-bg-color-surface-sunken tw-font-montserrat tw-text-[16px] tw-font-semibold tw-tracking-[0.08px] tw-text-dt-text-action tw-shadow-elevation-card"
        (click)="resetOrder()"
      >
        {{ "resetOrder" | i18n }}
      </button>
      <button
        type="button"
        class="tw-flex tw-h-12 tw-w-[200px] tw-shrink-0 tw-items-center tw-justify-center tw-rounded-2xl tw-border-none tw-bg-dt-surface-on-action tw-font-montserrat tw-text-[16px] tw-font-semibold tw-tracking-[0.08px] tw-text-dt-text-on-action tw-shadow-elevation-card"
        (click)="save()"
      >
        {{ "save" | i18n }}
      </button>
    </div>
  </div>
</div>
